import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { CheckCircle, Circle, Clock, Star, StarOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TaskSection = ({ conversationId, callId, onTaskUpdate, variant = 'chat' }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (conversationId || callId) {
      loadTasks();
    }
  }, [conversationId, callId]);

  const loadTasks = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('todo_items')
        .select('*')
        .eq('account_id', user.id)
        .order('created_at', { ascending: false });

      // Filter by either conversation ID or call ID
      if (conversationId) {
        query = query.eq('chat_conversation_id', conversationId);
      } else if (callId) {
        query = query.eq('call_id', callId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskCompletion = async (taskId, isCompleted) => {
    try {
      const updateData = {
        is_completed: !isCompleted,
        completed_at: !isCompleted ? new Date().toISOString() : null,
      };

      const { error } = await supabase
        .from('todo_items')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      const updatedTasks = tasks.map(task => 
        task.id === taskId 
          ? { ...task, ...updateData }
          : task
      );
      setTasks(updatedTasks);

      // Notify parent component about the update
      if (onTaskUpdate) {
        onTaskUpdate(updatedTasks);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const toggleTaskStar = async (taskId, isStarred) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .update({ is_starred: !isStarred })
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, is_starred: !isStarred }
          : task
      ));
    } catch (error) {
      console.error('Error updating task star:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTaskDescription = (task) => {
    // Use the action field from task_schema JSONB
    if (task.task_schema?.action) {
      return task.task_schema.action;
    }
    return 'Task item';
  };

  if (loading) {
    return (
      <div className="p-4 border-t border-gray-100">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    // Always render a spacer on mobile for call variant to prevent content from hiding behind header
    if (variant === 'call') {
      return (
        <div 
          className="md:hidden w-full flex-shrink-0" 
          style={{ height: 'var(--mobile-header-height, 7rem)' }}
        />
      );
    }
    return null; // Don't render if no tasks for chat variant
  }

  const containerClasses = variant === 'chat' 
    ? "sticky top-0 z-30 border-t border-gray-200/50 bg-white/85 backdrop-blur-md backdrop-saturate-150 shadow-sm"
    : "sticky z-30 border-t border-gray-200/50 bg-white/85 backdrop-blur-md backdrop-saturate-150 shadow-sm md:top-0";

  return (
    <div 
      className={containerClasses}
      style={variant === 'call' ? { top: 'var(--mobile-header-height, 7rem)' } : {}}
    >
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="relative">
            <div className={`w-2 h-2 rounded-full ${
              tasks.some(task => !task.is_completed && task.task_schema?.priority === 'urgent')
                ? 'bg-red-500'
                : tasks.some(task => !task.is_completed)
                  ? 'bg-amber-500'
                  : 'bg-gray-300'
            }`}></div>
            {tasks.some(task => !task.is_completed) && (
              <div className={`absolute inset-0 rounded-full animate-ping ${
                tasks.some(task => !task.is_completed && task.task_schema?.priority === 'urgent')
                  ? 'bg-red-500'
                  : 'bg-amber-500'
              }`}></div>
            )}
          </div>
          <h3 className="text-sm font-medium text-gray-900">Tasks</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {tasks.filter(t => !t.is_completed).length} pending
          </span>
        </div>

        <div className="space-y-2">
          <AnimatePresence>
            {tasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  task.is_completed 
                    ? 'bg-white/90 border-gray-200/60 backdrop-blur-sm shadow-sm' 
                    : 'bg-white/95 border-gray-300/70 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow'
                }`}
              >
                <button
                  onClick={() => toggleTaskCompletion(task.id, task.is_completed)}
                  className={`flex-shrink-0 mt-0.5 transition-colors ${
                    task.is_completed 
                      ? 'text-green-500 hover:text-green-600' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {task.is_completed ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${
                    task.is_completed 
                      ? 'text-gray-500 line-through' 
                      : 'text-gray-900'
                  }`}>
                    {getTaskDescription(task)}
                  </p>

                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(task.created_at)}</span>
                    </div>

                    {task.completed_at && (
                      <div className="text-xs text-green-600">
                        Completed {formatDate(task.completed_at)}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => toggleTaskStar(task.id, task.is_starred)}
                  className={`flex-shrink-0 p-1 transition-colors ${
                    task.is_starred 
                      ? 'text-yellow-500 hover:text-yellow-600' 
                      : 'text-gray-300 hover:text-gray-400'
                  }`}
                >
                  {task.is_starred ? (
                    <Star className="w-3 h-3 fill-current" />
                  ) : (
                    <StarOff className="w-3 h-3" />
                  )}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default TaskSection;